import { PRESENCE_MERGE, PRESENCE_CHANGED, PRESENCE_RESET, LOGOUT_SUCCESS } from '../constants';

/**
 * Who is online, as one map the whole app reads.
 *
 * This replaces two independent copies of the same fact. `MessagesSideBar` kept
 * `useState({})` fed by `online:user-list` and derived a status from it with its own
 * thresholds (ONLINE under 5 minutes of `last_seen`, AWAY under 30). `MessagesChat` kept
 * a separate `isUserOnline` boolean fed by the same event. Consequences:
 *
 *   • The two disagreed. `last_seen` is written on DISCONNECT, so the sidebar showed a
 *     green dot for five minutes after someone closed their laptop while the header,
 *     which had no `last_seen` fallback at all, showed them offline.
 *   • The pane's boolean started `false` and only ever moved on a broadcast — which
 *     fires on somebody else's connect. Opening a chat with an online peer therefore
 *     showed "Last seen …" until an unrelated user connected.
 *   • Neither reset on switching conversation, so a stale green dot followed you from an
 *     online peer to an offline one.
 *
 * The status strings are the server's (`constants.userOnlineStatus`): ONLINE, AWAY,
 * OFFLINE, LOGOUT. The client no longer derives them — one definition, on the server.
 */
const initialState = {
  /** `{ [userId]: { status, last_seen } }`. Absent id means "not known yet". */
  byUser: {},
};

const PresenceReducer = (state = initialState, action) => {
  switch (action.type) {
    /**
     * A batch, from a `presence:subscribe` ack or an HTTP payload that carried
     * `user_online_status`. Merged rather than replacing: the sidebar and the open
     * conversation subscribe to overlapping but different id sets, and a replace would
     * make whichever rendered last blank the other's rows.
     */
    case PRESENCE_MERGE: {
      const incoming = action.payload;
      if (!incoming || typeof incoming !== 'object') return state;

      const ids = Object.keys(incoming);
      if (ids.length === 0) return state;

      // Only allocate a new state when something actually changed — this runs on every
      // subscribe, and a fresh object identity would re-render every consumer.
      let changed = false;
      const byUser = { ...state.byUser };

      for (const id of ids) {
        const next = incoming[id];
        if (!next?.status) continue;
        const held = byUser[id];
        if (held?.status === next.status && held?.last_seen === (next.last_seen ?? null)) continue;
        byUser[id] = { status: next.status, last_seen: next.last_seen ?? null };
        changed = true;
      }

      return changed ? { ...state, byUser } : state;
    }

    /** One user, from a `presence:changed` push. */
    case PRESENCE_CHANGED: {
      const { user_id, status, last_seen = null } = action.payload ?? {};
      if (!user_id || !status) return state;

      const held = state.byUser[user_id];
      if (held?.status === status && held?.last_seen === last_seen) return state;

      return { ...state, byUser: { ...state.byUser, [user_id]: { status, last_seen } } };
    }

    /**
     * Drop everything. Used when the socket goes away: a presence map held across a
     * disconnect is a claim about live connections made by a client that has none, and it
     * would be shown as fact until the reconnect happened to contradict it.
     */
    case PRESENCE_RESET:
    case LOGOUT_SUCCESS:
      return initialState;

    default:
      return state;
  }
};

export default PresenceReducer;
